import { useQuery } from '@tanstack/react-query';

import { DataLakes } from '@graylog/enterprise-api';

import FieldTypeMapping from 'views/logic/fieldtypes/FieldTypeMapping';
import { FieldTypes } from 'views/logic/fieldtypes/FieldType';
import type { DataLakeField } from 'data-lake/Types';
import useSelectedDataLake from 'data-lake/hooks/useSelectedDataLake';
import { INTERNAL_DATA_LAKE_ID } from 'data-lake/preview/storage-types/internal/Constants';
import type { DataLakeFieldsFetchQuery } from 'data-lake/preview/Types';
import { defaultCompare } from 'logic/DefaultCompare';

const NON_FILTERABLE_FIELD_TYPES = [FieldTypeMapping.create('timestamp', FieldTypes.DATE())];

const mapBackendType = (fieldType: string) => {
  switch (fieldType) {
    case 'org.joda.time.DateTime':
      return 'date';
    case 'java.lang.Integer':
      return 'int';
    case 'java.lang.Boolean':
      return 'boolean';
    case 'java.lang.String':
      return 'string';
    default:
      return 'unknown';
  }
};

const identifyFieldType = (fieldType: string, fieldName: string) => {
  if (fieldName === 'streams') {
    return 'streams';
  }

  if (fieldName === 'gl2_source_input') {
    return 'input';
  }

  if (fieldName === 'associated_assets') {
    return 'associated-assets';
  }

  if (['source_ip', 'associated_ip', 'destination_ip'].includes(fieldName)) {
    return 'ip';
  }

  return mapBackendType(fieldType);
};

const deserializeFieldTypes = (fieldTypes: Array<DataLakeField> = []) =>
  fieldTypes.map(({ field_name: fieldName, class: fieldType }) =>
    FieldTypeMapping.fromJSON({
      name: fieldName,
      type: {
        type: identifyFieldType(fieldType, fieldName),
        properties: ['enumerable'],
        index_names: [],
      },
    }),
  );

const fetchFields = async (
  dataLakeId: string,
  tableId: string | undefined,
): Promise<{
  filterableFields: Array<FieldTypeMapping>;
  widgetFields: Array<FieldTypeMapping>;
  allFieldTypes: Array<FieldTypeMapping>;
  inMemoryFields: Set<string>;
}> => {
  const nonDeserializedData = await DataLakes.getFields(dataLakeId, tableId);

  const notFilterableFieldsSet = new Set(
    nonDeserializedData?.filter(({ filtering }) => filtering === 'NONE').map(({ field_name }) => field_name),
  );

  const data = deserializeFieldTypes(nonDeserializedData).sort((a, b) => defaultCompare(a.name, b.name));

  const widgetFields =
    data?.filter((field) => !NON_FILTERABLE_FIELD_TYPES.find((nonFilterable) => nonFilterable.name === field.name)) ??
    [];

  const filterableFields = widgetFields?.filter((field) => !notFilterableFieldsSet.has(field.name)) ?? [];

  const inMemoryFields = new Set(
    nonDeserializedData
      ?.filter(({ filtering }: DataLakeField) => filtering === 'IN_MEMORY')
      .map(({ field_name }: DataLakeField) => field_name) ?? [],
  );

  return {
    allFieldTypes: data ? [...NON_FILTERABLE_FIELD_TYPES, ...data] : [],
    widgetFields,
    filterableFields,
    inMemoryFields,
  };
};

const useFieldTypes = (
  fetchQueryOverride?: DataLakeFieldsFetchQuery,
  enabled = true,
): {
  data: Array<FieldTypeMapping>;
  filterableFields: Array<FieldTypeMapping>;
  widgetFields: Array<FieldTypeMapping>;
  refetch: () => void;
  isLoading?: boolean;
  isPending?: boolean;
  inMemoryFields: Set<string>;
} => {
  const { selectedDataLake, dataLakeFieldsFetchQuery } = useSelectedDataLake();
  const fetchQuery = fetchQueryOverride ?? dataLakeFieldsFetchQuery;
  const dataLakeId = selectedDataLake?.id;

  const {
    data: { allFieldTypes, filterableFields, widgetFields, inMemoryFields },
    refetch,
    isLoading,
    isPending,
  } = useQuery<{
    filterableFields: Array<FieldTypeMapping>;
    allFieldTypes: Array<FieldTypeMapping>;
    widgetFields: Array<FieldTypeMapping>;
    inMemoryFields: Set<string>;
  }>({
    queryKey: ['data-lake', 'filterable-fields', dataLakeId, fetchQuery?.tableId],
    queryFn: () => fetchFields(dataLakeId, fetchQuery?.tableId),
    staleTime: 30000,
    enabled: enabled && !!dataLakeId && (dataLakeId === INTERNAL_DATA_LAKE_ID || !!fetchQuery?.tableId),
    placeholderData: { filterableFields: [], allFieldTypes: [], inMemoryFields: new Set(), widgetFields: [] },
  });

  return {
    data: allFieldTypes,
    filterableFields,
    refetch,
    isLoading,
    isPending,
    inMemoryFields,
    widgetFields,
  };
};

export default useFieldTypes;
